import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { A4, CardBoxes, QR_QUIET, Rect, SheetLayout, cardsPerPage, qrMatrix, qrPath, sheetPages } from './showroom-sheet';
import type { SheetCard } from './showroom-pdf';

const MM_TO_PX = 96 / 25.4;
const FONT = '"Geist", "Helvetica Neue", Helvetica, Arial, sans-serif';

const place = (r: Rect, origin: Rect): React.CSSProperties => ({
    position: 'absolute',
    left: `${r.x - origin.x}mm`,
    top: `${r.y - origin.y}mm`,
    width: `${r.w}mm`,
    height: `${r.h}mm`,
});

/** The code as one SVG path: vector, so it is as sharp printed as on screen. */
export function QrSvg({ text, title }: { text: string; title?: string }) {
    const matrix = useMemo(() => qrMatrix(text), [text]);
    if (!matrix.length) return null;
    const size = matrix.length + 2 * QR_QUIET;
    return (
        <svg viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges" role="img" aria-label={title} style={{ display: 'block', width: '100%', height: '100%', background: '#fff' }}>
            <path d={qrPath(matrix)} fill="#000" />
        </svg>
    );
}

function Card({ boxes, card, layout, cta, storeName, dir }: { boxes: CardBoxes; card: SheetCard; layout: SheetLayout; cta: string; storeName?: string | null; dir: 'ltr' | 'rtl' }) {
    const { frame, photo, qr, text } = boxes;
    const type = layout.type;
    return (
        <div
            data-sheet-card
            style={{
                position: 'absolute',
                left: `${frame.x}mm`,
                top: `${frame.y}mm`,
                width: `${frame.w}mm`,
                height: `${frame.h}mm`,
                border: '0.2mm solid #d4d4d4',
                borderRadius: '2mm',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            <div style={{ ...place(photo, frame), background: '#f2f2f0', borderRadius: '1.2mm', overflow: 'hidden' }}>
                {card.imageUrl && <img src={card.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
            </div>
            <div style={place(qr, frame)}>
                <QrSvg text={card.url} title={card.name} />
            </div>
            <div dir={dir} style={{ ...place(text, frame), display: 'flex', flexDirection: 'column', textAlign: 'start', color: '#111' }}>
                <div
                    style={{
                        fontSize: `${type.name}pt`,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                    }}
                    dir="auto"
                >
                    {card.name}
                </div>
                {card.price && <div style={{ fontSize: `${type.price}pt`, marginTop: '1mm', fontVariantNumeric: 'tabular-nums' }}>{card.price}</div>}
                <div style={{ marginTop: 'auto', fontSize: `${type.cta}pt`, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cta}</div>
                {storeName && (
                    <div style={{ marginTop: '0.6mm', fontSize: `${type.store}pt`, color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} dir="auto">
                        {storeName}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * The A4 sheets as they will print: laid out in millimetres from the same
 * numbers as the PDF, then scaled down to fit the column. Inline styles on
 * purpose — the Print button lifts these pages into a bare frame where the
 * app's stylesheet does not exist.
 */
export const ShowroomSheet = forwardRef<HTMLDivElement, {
    cards: SheetCard[];
    layout: SheetLayout;
    cta: string;
    storeName?: string | null;
    dir: 'ltr' | 'rtl';
    pageLabel: (page: number, total: number) => string;
}>(({ cards, layout, cta, storeName, dir, pageLabel }, ref) => {
    const box = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.6);
    const pages = useMemo(() => sheetPages(cards.length, layout), [cards.length, layout]);
    const per = cardsPerPage(layout);

    useEffect(() => {
        const el = box.current;
        if (!el) return undefined;
        const measure = () => setScale(Math.min(1, el.clientWidth / (A4.width * MM_TO_PX)));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div ref={box} className="w-full">
            <div ref={ref} className="flex flex-col gap-4">
                {pages.map((page, p) => (
                    <figure key={p} className="flex flex-col gap-1.5">
                        <div
                            // The sheet is paper: left-to-right geometry in every language (its text follows the language).
                            dir="ltr"
                            className="overflow-hidden rounded-sm ring-1 ring-foreground/10"
                            style={{ width: `${A4.width * MM_TO_PX * scale}px`, height: `${A4.height * MM_TO_PX * scale}px` }}
                        >
                            <div
                                data-sheet-page
                                dir="ltr"
                                style={{
                                    position: 'relative',
                                    width: `${A4.width}mm`,
                                    height: `${A4.height}mm`,
                                    background: '#fff',
                                    fontFamily: FONT,
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'top left',
                                    overflow: 'hidden',
                                }}
                            >
                                {page.map((boxes, i) => (
                                    <Card key={cards[p * per + i].key} boxes={boxes} card={cards[p * per + i]} layout={layout} cta={cta} storeName={storeName} dir={dir} />
                                ))}
                            </div>
                        </div>
                        {pages.length > 1 && <figcaption className="text-xs text-muted-foreground">{pageLabel(p + 1, pages.length)}</figcaption>}
                    </figure>
                ))}
            </div>
        </div>
    );
});
ShowroomSheet.displayName = 'ShowroomSheet';
